import { SelectSubCategoriesModal } from '../../../components/modal/app_modals/selectSubCategoriesModal'
import { ListMenuPreview } from './list-menu-preview'
import { DndContext, DragEndEvent, closestCenter } from '@dnd-kit/core'
import { restrictToVerticalAxis } from '@dnd-kit/modifiers'
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  ArrowDown,
  ArrowUp,
  ChevronDown,
  ChevronRight,
  Clock3,
  Edit3,
  GripVertical,
  Plus,
  Trash2,
  X,
} from 'lucide-react'
import { FormEvent, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { ConfirmationModal } from '../../../components/modal/app_modals/confirmationModal'
import { PageHeader } from '../../../components/typography/PageHeader'
import { moveElement } from '../../../helpers'
import Layout from '../../../layout'
import {
  useCreateCurationSectionMutation,
  useCreateCurationTimeSlotGroupMutation,
  useDeleteCurationSectionMutation,
  useGetCurationSectionsQuery,
  useGetHomepageCurationTypesQuery,
  usePreviewCurationAutomatedQuery,
  usePreviewCurationKitchensQuery,
  usePreviewCurationCategoryQuery,
  usePreviewCurationSubcategoryQuery,
  usePublishCurationSectionsMutation,
  useReorderCurationSectionsMutation,
  useUpdateCurationSectionMutation,
  useUpdateCurationTimeSlotGroupMutation,
} from '../../../redux-toolkits/home-page-v2.1/homePageV21.slice'
import {
  AutomatedCurationFilters,
  AutomatedCurationSort,
  CurationPage,
  CurationContentType,
  HomepageCurationType,
  HomepageSection,
  HomepageSectionItem,
  HomepageSectionType,
} from '../../../redux-toolkits/home-page-v2.1/homePageV21.type'
import { useGetFulfilmentCentersQuery } from '../../../redux-toolkits/vendors/vendors.slice'
import { ContentPickerModals, EditorContentItem } from './contentPicker'
import { getHomepageSectionsQueryOptions } from './homepage-sections-query'
import {
  automatedSourceLabel,
  getSortOptions,
  sortForSection,
  sortLabels,
} from './automated-curation-filters'

const inputClass =
  'w-full rounded-lg border border-[#E4E4E4] bg-white px-3 py-2.5 text-sm outline-none focus:border-black'
const secondaryButtonClass =
  'rounded-lg border border-[#D8D8D8] bg-white px-3 py-2 text-sm font-medium hover:bg-[#F7F7F7] disabled:opacity-50'
const primaryButtonClass =
  'rounded-lg bg-black px-4 py-2 text-sm font-medium text-white hover:bg-[#252525] disabled:opacity-50'

const sectionTypeLabels: Record<HomepageSectionType, string> = {
  KITCHENS_NEAR_YOU: 'Kitchens near you',
  CATEGORY: 'Category section',
  SUBCATEGORY: 'Subcategory cards (links only)',
  MANUAL_CURATION: 'Manual curation',
  AUTOMATED_CURATION: 'Automatic curation',
  CATEGORY_CURATION: 'Products from a category',
  SUBCATEGORY_CURATION: 'Products from a subcategory',
  MARKETING_BANNER: 'Marketing banners',
}

const contentTypeLabels: Partial<Record<CurationContentType, string>> = {
  PRODUCTS: 'Products',
  EVENTS: 'Events',
  ESSENTIALS: 'Curated Essentials',
  GOOD_LISTS: 'Good Lists',
  LIST_MENUS: 'List menus',
  CREATORS: 'Creators',
  CATEGORIES: 'Categories',
  SUBCATEGORIES: 'Subcategories',
  HOMEPAGE_BANNER: 'Banner',
  MANUAL_ITEM: 'Custom card',
}

const hasOnlyListMenus = (items: Pick<HomepageSectionItem, 'type'>[]) =>
  items.length > 0 && items.every((item) => item.type === 'LIST_MENUS')

const selectableContentTypes: CurationContentType[] = [
  'HOMEPAGE_BANNER',
  'PRODUCTS',
  'GOOD_LISTS',
  'LIST_MENUS',
  'ESSENTIALS',
  'CREATORS',
  'EVENTS',
  'CATEGORIES',
  'SUBCATEGORIES',
  'MANUAL_ITEM',
]

interface ScheduleDraft {
  selected: boolean
  startTime: string
  endTime: string
}

const keyFor = () => `${Date.now()}-${Math.random()}`

const errorMessage = (error: unknown): string => {
  const requestError = error as {
    data?: { message?: string; error?: string }
    error?: string
  }
  return (
    requestError.data?.message ||
    requestError.data?.error ||
    requestError.error ||
    'The request could not be completed'
  )
}

const resolvedItem = (item: HomepageSectionItem): Record<string, any> => {
  if (typeof item.item === 'object' && item.item !== null) return item.item
  if (item.homepageBanner) return item.homepageBanner
  if (item.manualContent) return item.manualContent
  return {}
}

const imageFor = (item: Record<string, any>): string | undefined =>
  item.bannerUrl ||
  item.banner ||
  item.image ||
  item.logo ||
  item.icon ||
  item.images?.[0] ||
  item.productImages?.[0]

const ContentCard = ({
  item,
  type,
}: {
  item: Record<string, any>
  type: CurationContentType
}) => (
  <div className="min-w-0">
    <div className="aspect-[4/3] overflow-hidden rounded-xl bg-[#F1F1F1]">
      {imageFor(item) ? (
        <img
          alt=""
          className="h-full w-full object-cover"
          src={imageFor(item)}
        />
      ) : null}
    </div>
    <p className="mt-2 truncate text-sm font-semibold">
      {item.title || item.name || contentTypeLabels[type]}
    </p>
    <p className="mt-1 truncate text-xs text-[#666]">
      {type === 'LIST_MENUS'
        ? `${item.listCount ?? 0} published lists`
        : item.readingTime ||
          item.descriptionExcerpt ||
          item.description ||
          contentTypeLabels[type]}
    </p>
  </div>
)

const BannerPreview = ({ banner }: { banner: Record<string, any> }) => (
  <div
    className="relative min-h-64 overflow-hidden rounded-xl bg-[#222] bg-cover bg-center p-6 text-white"
    style={
      banner.desktopImage
        ? { backgroundImage: `url(${banner.desktopImage})` }
        : undefined
    }
  >
    <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
    <div className="relative flex min-h-52 flex-col justify-end">
      <p className="font-recoleta text-3xl font-semibold">{banner.heading}</p>
      {banner.subtext ? (
        <p className="mt-2 max-w-md text-sm">{banner.subtext}</p>
      ) : null}
      <div className="mt-4 flex gap-2">
        {banner.buttonText ? (
          <span className="rounded-lg bg-white px-4 py-2 text-xs font-medium text-black">
            {banner.buttonText}
          </span>
        ) : null}
        {banner.secondaryButtonText ? (
          <span className="rounded-lg border border-white px-4 py-2 text-xs font-medium">
            {banner.secondaryButtonText}
          </span>
        ) : null}
      </div>
    </div>
  </div>
)

const CreatorPreview = ({ items }: { items: HomepageSectionItem[] }) => (
  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
    {items.slice(0, 4).map((item, index) => {
      const creator = resolvedItem(item)
      const images = Array.isArray(creator.bestSellingProductImages)
        ? creator.bestSellingProductImages.slice(0, 3)
        : []
      return (
        <div
          className="rounded-xl border border-[#ECECEC] bg-white p-4"
          key={item._id || `${creator._id || 'creator'}-${index}`}
        >
          <div className="mb-4 flex items-center gap-3">
            <img
              alt=""
              className="h-11 w-11 rounded-full bg-[#F2F2F2] object-cover"
              src={creator.logo || creator.banner}
            />
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold">
                {creator.name || 'Creator'}
              </p>
              <p className="text-xs text-[#666]">
                {creator.category?.name || 'Creator'}
              </p>
            </div>
          </div>
          <div className="mb-4 grid grid-cols-3 gap-2">
            {[0, 1, 2].map((imageIndex) => (
              <div
                className="aspect-square overflow-hidden rounded-lg bg-[#F2F2F2]"
                key={imageIndex}
              >
                {images[imageIndex] ? (
                  <img
                    alt=""
                    className="h-full w-full object-cover"
                    src={images[imageIndex]}
                  />
                ) : null}
              </div>
            ))}
          </div>
          <p className="line-clamp-2 text-xs leading-5 text-[#444]">
            {creator.extendedBio || 'Creator description'}
          </p>
        </div>
      )
    })}
  </div>
)

const SectionPreview = ({
  items,
  sectionType,
  stacked = false,
  title,
  description,
}: {
  title?: string
  description?: string
  items: HomepageSectionItem[]
  sectionType: HomepageSectionType
  stacked?: boolean
}) => {
  const banners = items.filter((item) => item.type === 'HOMEPAGE_BANNER')
  const nonBanners = items.filter((item) => item.type !== 'HOMEPAGE_BANNER')
  const creators = nonBanners.filter((item) => item.type === 'CREATORS')

  if (sectionType === 'KITCHENS_NEAR_YOU') {
    return (
      <div className="space-y-4">
        <p className="rounded-xl bg-[#F7F7F7] p-4 text-sm text-[#666]">
          Admin shows all {creators.length} Food kitchens in this storefront,
          including closed and inactive kitchens. Customers see only open,
          active kitchens with food available for delivery to their selected
          location.
        </p>
        {creators.length ? (
          <div className="grid max-h-[32rem] gap-4 overflow-y-auto sm:grid-cols-2 xl:grid-cols-4">
            {creators.map((entry) => {
              const kitchen = resolvedItem(entry)
              return (
                <div
                  key={kitchen._id}
                  className="rounded-xl border border-[#ECECEC] bg-white p-4"
                >
                  {kitchen.logo || kitchen.banner ? (
                    <img
                      src={kitchen.logo || kitchen.banner}
                      alt=""
                      className="mb-3 h-14 w-14 rounded-full object-cover"
                    />
                  ) : null}
                  <p className="font-semibold">{kitchen.name || 'Kitchen'}</p>
                  <p className="mt-1 text-xs text-[#666]">
                    {kitchen.status} ·{' '}
                    {kitchen.isOpen ? 'Open now' : 'Closed now'}
                  </p>
                  <p className="mt-2 line-clamp-2 text-xs text-[#666]">
                    {kitchen.description}
                  </p>
                </div>
              )
            })}
          </div>
        ) : (
          <p className="text-sm text-[#666]">
            No Food kitchens are assigned to this storefront yet.
          </p>
        )}
      </div>
    )
  }
  if (
    sectionType === 'CATEGORY' &&
    items.some((item) => item.subcategories !== undefined)
  ) {
    return (
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item, index) => {
          const category = resolvedItem(item)
          return (
            <div
              key={item._id || index}
              className="flex items-center gap-3 rounded-xl border border-[#EFEFEF] p-5"
            >
              {category.icon ? (
                <img
                  src={category.icon}
                  alt=""
                  className="h-9 w-9 rounded-full object-contain"
                />
              ) : (
                <span className="h-9 w-9 rounded-full bg-orange-100" />
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium">{category.name}</p>
                <p className="mt-1 text-xs text-[#888]">
                  {item.subcategories
                    ?.map((subcategory) =>
                      typeof subcategory === 'string' ? '' : subcategory.name,
                    )
                    .filter(Boolean)
                    .join(', ')}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-[#666]" />
            </div>
          )
        })}
      </div>
    )
  }

  if (sectionType === 'MARKETING_BANNER') {
    return banners.length ? (
      <div className="grid gap-4 lg:grid-cols-2">
        {banners.map((item, index) => (
          <BannerPreview banner={resolvedItem(item)} key={item._id || index} />
        ))}
      </div>
    ) : (
      <p className="rounded-xl bg-[#F7F7F7] p-8 text-center text-sm text-[#666]">
        No banners added.
      </p>
    )
  }

  if (creators.length && creators.length === nonBanners.length) {
    return <CreatorPreview items={creators} />
  }

  if (hasOnlyListMenus(items)) {
    return (
      <ListMenuPreview items={items} title={title} description={description} />
    )
  }

  const cards = nonBanners
  if (banners.length) {
    return stacked ? (
      <div className="grid gap-4 lg:grid-cols-[2fr_1fr]">
        <BannerPreview banner={resolvedItem(banners[0])} />
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
          {cards.slice(0, 2).map((item, index) => (
            <ContentCard
              item={resolvedItem(item)}
              key={item._id || index}
              type={item.type}
            />
          ))}
        </div>
      </div>
    ) : (
      <div className="space-y-4">
        <BannerPreview banner={resolvedItem(banners[0])} />
        {cards.length ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {cards.map((item, index) => (
              <ContentCard
                item={resolvedItem(item)}
                key={item._id || index}
                type={item.type}
              />
            ))}
          </div>
        ) : null}
      </div>
    )
  }

  return cards.length ? (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((item, index) => (
        <ContentCard
          item={resolvedItem(item)}
          key={item._id || index}
          type={item.type}
        />
      ))}
    </div>
  ) : (
    <p className="rounded-xl bg-[#F7F7F7] p-8 text-center text-sm text-[#666]">
      No curated content added.
    </p>
  )
}

const CustomerSectionPreview = ({
  items,
  sectionType,
  stacked = false,
  isScrollable = true,
  title,
  description,
}: {
  title?: string
  description?: string
  items: HomepageSectionItem[]
  sectionType: HomepageSectionType
  stacked?: boolean
  isScrollable?: boolean
}) => (
  <>
    <SectionPreview
      items={items}
      sectionType={sectionType}
      stacked={stacked}
      title={title}
      description={description}
    />
    {/* {!isScrollable ? (
      <div className="mt-3 flex justify-end">
        <span className="rounded-lg border border-[#DADADA] bg-white px-3 py-2 text-xs font-semibold text-[#333]">
          See all →
        </span>
      </div>
    ) : null} */}
  </>
)

const editorItemsToPreview = (
  items: EditorContentItem[],
): HomepageSectionItem[] =>
  items.map((item) => ({
    type: item.type,
    item: item.value,
    subcategories: item.subcategories,
    homepageBanner: item.banner,
    manualContent: item.manualContent,
  }))

const automatedPreviewSummary = ({
  count,
  maxItems,
  sort,
  salesLookbackDays,
  freeOnly,
  curationType,
}: {
  count?: number
  maxItems: number
  sort: AutomatedCurationSort
  salesLookbackDays: number
  freeOnly: boolean
  curationType?: HomepageCurationType
}) => {
  const countLabel =
    count === undefined
      ? 'Checking products'
      : `Showing ${count} ${count === 1 ? 'product' : 'products'}`
  const sourceLabel = automatedSourceLabel({
    sort,
    salesLookbackDays,
    productSection: curationType?.productSection,
  })
  const freeLabel = freeOnly ? ' · Free only' : ''
  const curationLabel = curationType?.name
    ? ` · Section: ${curationType.name}`
    : ''

  return `${countLabel} · Max or limit: ${maxItems} · Sorted by: ${sortLabels[sort]} · ${sourceLabel}${freeLabel}${curationLabel}`
}

// Resolves and renders the live products for a saved automated section so its
// preview matches what customers see, not just a filter summary.
const AutomatedSectionPreview = ({
  curationType,
  isScrollable,
  filters,
  fulfilmentCentreId,
  page,
}: {
  curationType?: HomepageCurationType
  isScrollable?: boolean
  filters?: AutomatedCurationFilters
  fulfilmentCentreId: string
  page: CurationPage
}) => {
  const curationTypeId = curationType?._id ?? ''
  const sort = sortForSection(filters?.sort, curationType?.productSection)
  const maxItems = filters?.maxItems || 8
  const freeOnly = Boolean(filters?.freeOnly)
  const { data, isFetching } = usePreviewCurationAutomatedQuery(
    {
      curationTypeId,
      filters: {
        sort,
        maxItems,
        salesLookbackDays: filters?.salesLookbackDays || 90,
        freeOnly,
      },
      fulfilmentCentreId,
      page,
    },
    { skip: !curationTypeId },
  )

  return (
    <div>
      <p className="mb-3 text-xs text-[#666]">
        {automatedPreviewSummary({
          count: isFetching ? undefined : data?.count,
          maxItems,
          sort,
          salesLookbackDays: filters?.salesLookbackDays || 90,
          freeOnly,
          curationType,
        })}
      </p>
      {isFetching ? (
        <p className="rounded-xl bg-[#F7F7F7] p-8 text-center text-sm text-[#666]">
          Loading preview…
        </p>
      ) : data && data.items.length ? (
        <CustomerSectionPreview
          items={data.items.slice(0, maxItems)}
          sectionType="AUTOMATED_CURATION"
          isScrollable={isScrollable}
        />
      ) : (
        <p className="rounded-xl bg-[#F7F7F7] p-8 text-center text-sm text-[#666]">
          No products match these filters yet.
        </p>
      )}
    </div>
  )
}

const SortableEditorItem = ({
  item,
  onEdit,
  onRemove,
}: {
  item: EditorContentItem
  onEdit: () => void
  onRemove: () => void
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.key })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  }

  const isEditable =
    item.type === 'HOMEPAGE_BANNER' || item.type === 'MANUAL_ITEM'

  const image =
    item.type === 'HOMEPAGE_BANNER'
      ? item.banner?.desktopImage
      : item.type === 'MANUAL_ITEM'
        ? item.manualContent?.image
        : item.value && imageFor(item.value)

  const title =
    item.type === 'HOMEPAGE_BANNER'
      ? item.banner?.heading
      : item.type === 'MANUAL_ITEM'
        ? item.manualContent?.title
        : item.value?.title || item.value?.name || contentTypeLabels[item.type]

  return (
    <div
      className={`flex items-center justify-between rounded-lg border border-[#E7E7E7] bg-white p-3 ${
        isDragging ? 'shadow-lg' : ''
      }`}
      ref={setNodeRef}
      style={style}
    >
      <div className="flex min-w-0 items-center gap-3">
        <button
          className="cursor-grab touch-none rounded-lg p-1 text-[#999] hover:bg-[#F3F3F3] active:cursor-grabbing"
          type="button"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="h-12 w-12 overflow-hidden rounded-lg bg-[#F1F1F1]">
          {image ? (
            <img alt="" className="h-full w-full object-cover" src={image} />
          ) : null}
        </div>
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">
            {title || contentTypeLabels[item.type]}
          </p>
          <p className="text-xs text-[#777]">
            {contentTypeLabels[item.type]}
            {item.type === 'LIST_MENUS'
              ? ` · ${item.value?.listCount ?? 0} published lists`
              : ''}
          </p>
        </div>
      </div>
      <div className="flex gap-1">
        {isEditable ? (
          <button
            className="rounded-lg p-2 hover:bg-[#F3F3F3]"
            onClick={onEdit}
            type="button"
          >
            <Edit3 className="h-4 w-4" />
          </button>
        ) : null}
        <button
          className="rounded-lg p-2 text-[#A22] hover:bg-red-50"
          onClick={onRemove}
          type="button"
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}

export const GoodHomeV21Page = ({
  page = CurationPage.HOME,
}: {
  page?: CurationPage
}) => {
  const [subcategoryCardKey, setSubcategoryCardKey] = useState<string>()
  const {
    data: curationTypes = [],
    isLoading: isLoadingCurationTypes,
    isError: hasCurationTypeError,
    refetch: refetchTypes,
  } = useGetHomepageCurationTypesQuery()
  const { data: fulfilmentCentresResponse } = useGetFulfilmentCentersQuery({
    limit: 200,
  })
  const fulfilmentCentres = fulfilmentCentresResponse?.data?.docs ?? []
  const [activeCurationTypeId, setActiveCurationTypeId] = useState('')
  const [fulfilmentCentreId, setFulfilmentCentreId] = useState('')
  const [sections, setSections] = useState<HomepageSection[]>([])
  const [showEditor, setShowEditor] = useState(false)
  const [isCustomerPreviewCollapsed, setIsCustomerPreviewCollapsed] =
    useState(false)
  const [showScheduleEditor, setShowScheduleEditor] = useState(false)
  const [editingSection, setEditingSection] = useState<HomepageSection>()
  const [sectionPendingDeletion, setSectionPendingDeletion] =
    useState<HomepageSection>()
  const [sectionType, setSectionType] =
    useState<HomepageSectionType>('MANUAL_CURATION')
  const [stacked, setStacked] = useState(false)
  const [isScrollable, setIsScrollable] = useState(true)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [editorItems, setEditorItems] = useState<EditorContentItem[]>([])
  const [pickerType, setPickerType] = useState<CurationContentType | null>(null)
  const [bannerEditKey, setBannerEditKey] = useState<string>()
  const [sort, setSort] = useState<AutomatedCurationSort>('newest')
  const [maxItems, setMaxItems] = useState(8)
  const [salesLookbackDays, setSalesLookbackDays] = useState(90)
  const [freeOnly, setFreeOnly] = useState(false)
  const [groupName, setGroupName] = useState('')
  const [groupOrder, setGroupOrder] = useState(1)
  const [schedule, setSchedule] = useState<Record<string, ScheduleDraft>>({})
  const [editingGroupName, setEditingGroupName] = useState<string>()
  const [collapsedSections, setCollapsedSections] = useState<
    Record<string, boolean>
  >({})
  const editorRef = useRef<HTMLFormElement>(null)

  const toggleSectionCollapsed = (sectionId: string) =>
    setCollapsedSections((current) => ({
      ...current,
      [sectionId]: !current[sectionId],
    }))

  useEffect(() => {
    if (!activeCurationTypeId && curationTypes.length) {
      setActiveCurationTypeId(curationTypes[0]._id)
    }
  }, [activeCurationTypeId, curationTypes])

  useEffect(() => {
    if (!fulfilmentCentreId && fulfilmentCentres.length) {
      setFulfilmentCentreId(fulfilmentCentres[0]._id)
    }
  }, [fulfilmentCentreId, fulfilmentCentres])

  useEffect(() => {
    setShowEditor(false)
    setShowScheduleEditor(false)
    setEditingSection(undefined)
    setSectionPendingDeletion(undefined)
    setSubcategoryCardKey(undefined)
  }, [fulfilmentCentreId, activeCurationTypeId, page])

  useEffect(() => {
    if (showEditor) {
      editorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [showEditor, editingSection])

  const {
    data: homepageConfig,
    isLoading,
    isFetching,
  } = useGetCurationSectionsQuery(
    { curationTypeId: activeCurationTypeId, fulfilmentCentreId, page },
    getHomepageSectionsQueryOptions(
      !activeCurationTypeId || !fulfilmentCentreId,
    ),
  )

  useEffect(() => {
    setSections(homepageConfig?.sections || [])
  }, [homepageConfig])

  const { data: automatedPreview, isFetching: isPreviewingAutomated } =
    usePreviewCurationAutomatedQuery(
      {
        curationTypeId: activeCurationTypeId,
        filters: { sort, maxItems, salesLookbackDays, freeOnly },
        fulfilmentCentreId,
        page,
      },
      {
        skip:
          !showEditor ||
          sectionType !== 'AUTOMATED_CURATION' ||
          !activeCurationTypeId ||
          !fulfilmentCentreId,
      },
    )

  const selectedSubcategory = editorItems.find(
    (item) => item.type === 'SUBCATEGORIES',
  )?.value
  const selectedSubcategoryId = String(selectedSubcategory?._id || '')
  const {
    data: subcategoryPreview,
    isFetching: isPreviewingSubcategory,
    error: subcategoryPreviewError,
  } = usePreviewCurationSubcategoryQuery(
    {
      curationTypeId: activeCurationTypeId,
      subcategoryId: selectedSubcategoryId,
      fulfilmentCentreId,
      page,
    },
    {
      skip:
        !showEditor ||
        sectionType !== 'SUBCATEGORY_CURATION' ||
        !activeCurationTypeId ||
        !fulfilmentCentreId ||
        !selectedSubcategoryId,
    },
  )

  const selectedCategory = editorItems.find(
    (item) => item.type === 'CATEGORIES',
  )?.value
  const selectedCategoryId = String(selectedCategory?._id || '')
  const {
    data: categoryPreview,
    isFetching: isPreviewingCategory,
    error: categoryPreviewError,
  } = usePreviewCurationCategoryQuery(
    {
      curationTypeId: activeCurationTypeId,
      categoryId: selectedCategoryId,
      fulfilmentCentreId,
      page,
    },
    {
      skip:
        !showEditor ||
        sectionType !== 'CATEGORY_CURATION' ||
        !activeCurationTypeId ||
        !fulfilmentCentreId ||
        !selectedCategoryId,
    },
  )

  const [createSection, { isLoading: isCreatingSection }] =
    useCreateCurationSectionMutation()
  const [updateSection, { isLoading: isUpdatingSection }] =
    useUpdateCurationSectionMutation()
  const [deleteSection, { isLoading: isDeletingSection }] =
    useDeleteCurationSectionMutation()
  const [publishSections] = usePublishCurationSectionsMutation()
  const [reorderSections, { isLoading: isReordering }] =
    useReorderCurationSectionsMutation()
  const [createTimeGroup, { isLoading: isScheduling }] =
    useCreateCurationTimeSlotGroupMutation()
  const [updateTimeGroup, { isLoading: isUpdatingGroup }] =
    useUpdateCurationTimeSlotGroupMutation()

  const activeCurationType = useMemo(
    () => curationTypes.find((type) => type._id === activeCurationTypeId),
    [activeCurationTypeId, curationTypes],
  )
  const activeProductSection = activeCurationType?.productSection

  useEffect(() => {
    setSort((current) => sortForSection(current, activeProductSection))
  }, [activeProductSection])

  const {
    currentData: kitchenPreview,
    isFetching: isPreviewingKitchens,
    isError: kitchenPreviewFailed,
    refetch: refetchKitchenPreview,
  } = usePreviewCurationKitchensQuery(
    { curationTypeId: activeCurationTypeId, fulfilmentCentreId, page },
    {
      skip:
        !showEditor ||
        sectionType !== 'KITCHENS_NEAR_YOU' ||
        page !== CurationPage.BROWSE ||
        activeCurationType?.productSection !== 'FOOD' ||
        !activeCurationTypeId ||
        !fulfilmentCentreId,
      refetchOnMountOrArgChange: true,
    },
  )

  // Group scheduled sections by their slot group so each group can be
  // identified and edited as a whole.
  const timeGroups = useMemo(() => {
    const groups = new Map<
      string,
      { name: string; order: number; sections: HomepageSection[] }
    >()
    for (const section of sections) {
      const name = section.timeSchedule?.slotGroup
      if (!section.isTimeScheduled || !name) continue
      const existing = groups.get(name)
      if (existing) {
        existing.sections.push(section)
      } else {
        groups.set(name, {
          name,
          order: section.order,
          sections: [section],
        })
      }
    }
    return Array.from(groups.values()).sort((a, b) => a.order - b.order)
  }, [sections])

  const resetEditor = () => {
    setSubcategoryCardKey(undefined)
    setEditingSection(undefined)
    setTitle('')
    setDescription('')
    setSectionType('MANUAL_CURATION')
    setStacked(false)
    setIsScrollable(true)
    setEditorItems([])
    setSort('newest')
    setMaxItems(8)
    setSalesLookbackDays(90)
    setFreeOnly(false)
    setIsCustomerPreviewCollapsed(false)
  }

  const openCreateEditor = () => {
    resetEditor()
    setShowEditor(true)
  }

  const openEditEditor = (section: HomepageSection) => {
    setIsCustomerPreviewCollapsed(false)
    setEditingSection(section)
    setTitle(section.title)
    setDescription(section.description || '')
    setSectionType(section.sectionType)
    setStacked(Boolean(section.stacked))
    setIsScrollable(section.isScrollable !== false)
    setSort(
      sortForSection(section.filters?.sort, activeCurationType?.productSection),
    )
    setMaxItems(section.filters?.maxItems || 8)
    setSalesLookbackDays(section.filters?.salesLookbackDays || 90)
    setFreeOnly(Boolean(section.filters?.freeOnly))
    const configuredSubcategory = section.subcategoryCuration?.subcategory
    const configuredCategory = section.categoryCuration?.category
    if (
      section.sectionType === 'SUBCATEGORY_CURATION' &&
      configuredSubcategory
    ) {
      setEditorItems([
        {
          key: keyFor(),
          type: 'SUBCATEGORIES',
          value:
            typeof configuredSubcategory === 'object'
              ? configuredSubcategory
              : {
                  _id: configuredSubcategory,
                  name: 'Selected subcategory',
                },
        },
      ])
    } else if (
      section.sectionType === 'CATEGORY_CURATION' &&
      configuredCategory
    ) {
      setEditorItems([
        {
          key: keyFor(),
          type: 'CATEGORIES',
          value:
            typeof configuredCategory === 'object'
              ? configuredCategory
              : {
                  _id: configuredCategory,
                  name: 'Selected category',
                },
        },
      ])
    } else {
      setEditorItems(
        (section.items || []).map((item) => ({
          key: item._id || keyFor(),
          type: item.type,
          subcategories: item.subcategories?.filter(
            (subcategory): subcategory is Exclude<typeof subcategory, string> =>
              typeof subcategory !== 'string',
          ),
          value:
            typeof item.item === 'object'
              ? item.item
              : item.item
                ? { _id: item.item, name: 'Selected content' }
                : undefined,
          banner:
            item.homepageBanner ||
            (item.type === 'HOMEPAGE_BANNER'
              ? (resolvedItem(item) as any)
              : undefined),
          manualContent:
            item.manualContent ||
            (item.type === 'MANUAL_ITEM'
              ? (resolvedItem(item) as any)
              : undefined),
        })),
      )
    }
    setShowEditor(true)
  }

  const changeSectionType = (type: HomepageSectionType) => {
    setSectionType(type)
    if (type !== 'MANUAL_CURATION') setStacked(false)
    if (type === 'MARKETING_BANNER') setIsScrollable(true)
    if (type === 'AUTOMATED_CURATION' || type === 'KITCHENS_NEAR_YOU')
      setEditorItems([])
    if (type === 'CATEGORY_CURATION') {
      setEditorItems((items) =>
        items.filter((item) => item.type === 'CATEGORIES').slice(0, 1),
      )
    }
    if (type === 'SUBCATEGORY_CURATION') {
      setEditorItems((items) =>
        items.filter((item) => item.type === 'SUBCATEGORIES').slice(0, 1),
      )
    }
    if (type === 'MARKETING_BANNER') {
      setEditorItems((items) =>
        items.filter((item) => item.type === 'HOMEPAGE_BANNER').slice(0, 3),
      )
    }
    if (type === 'CATEGORY') {
      setEditorItems((items) =>
        items.filter((item) => item.type === 'CATEGORIES'),
      )
    }
    if (type === 'SUBCATEGORY') {
      setEditorItems((items) =>
        items.filter((item) => item.type === 'SUBCATEGORIES'),
      )
    }
  }

  const replaceType = (
    type: CurationContentType,
    values: Array<Record<string, any>>,
  ) => {
    setEditorItems((items) => [
      ...items.filter((item) => item.type !== type),
      ...values.map((value) => ({
        key: keyFor(),
        type,
        value,
        subcategories:
          items.find(
            (item) => item.type === type && item.value?._id === value._id,
          )?.subcategories ??
          (page === CurationPage.BROWSE && type === 'CATEGORIES'
            ? []
            : undefined),
      })),
    ])
  }

  const saveBanner = (
    banner: EditorContentItem['banner'],
    editKey?: string,
  ) => {
    if (!banner) return
    setEditorItems((items) => {
      if (editKey) {
        return items.map((item) =>
          item.key === editKey ? { ...item, banner } : item,
        )
      }
      const bannerLimit = sectionType === 'MARKETING_BANNER' ? 3 : 1
      if (
        items.filter((item) => item.type === 'HOMEPAGE_BANNER').length >=
        bannerLimit
      ) {
        toast.error(
          sectionType === 'MARKETING_BANNER'
            ? 'Marketing sections allow up to 3 banners'
            : 'Manual sections allow one banner',
        )
        return items
      }
      return [...items, { key: keyFor(), type: 'HOMEPAGE_BANNER', banner }]
    })
    setBannerEditKey(undefined)
  }

  const saveManualItem = (
    manualContent: EditorContentItem['manualContent'],
    editKey?: string,
  ) => {
    if (!manualContent) return
    setEditorItems((items) => {
      if (editKey) {
        return items.map((item) =>
          item.key === editKey ? { ...item, manualContent } : item,
        )
      }
      return [...items, { key: keyFor(), type: 'MANUAL_ITEM', manualContent }]
    })
    setBannerEditKey(undefined)
  }

  const removeEditorItem = (key: string) =>
    setEditorItems((items) => items.filter((item) => item.key !== key))

  const handleItemDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    setEditorItems((items) => {
      const oldIndex = items.findIndex((item) => item.key === active.id)
      const newIndex = items.findIndex((item) => item.key === over.id)
      if (oldIndex === -1 || newIndex === -1) return items
      return moveElement(items, oldIndex, newIndex)
    })
  }

  const openContentPicker = (type: CurationContentType, key?: string) => {
    setBannerEditKey(key)
    setPickerType(type)
  }

  const submitSection = async (event: FormEvent) => {
    event.preventDefault()
    if (!activeCurationTypeId || !fulfilmentCentreId) return
    if (
      sectionType !== 'AUTOMATED_CURATION' &&
      sectionType !== 'KITCHENS_NEAR_YOU' &&
      editorItems.length === 0
    ) {
      toast.error('Add content before saving the section')
      return
    }
    if (sectionType === 'SUBCATEGORY_CURATION' && !selectedSubcategoryId) {
      toast.error('Select a subcategory before saving the section')
      return
    }
    if (sectionType === 'CATEGORY_CURATION' && !selectedCategoryId) {
      toast.error('Select a category before saving the section')
      return
    }

    const items: HomepageSectionItem[] = editorItems.map((item) => {
      if (item.type === 'HOMEPAGE_BANNER') {
        return { type: item.type, homepageBanner: item.banner }
      }
      if (item.type === 'MANUAL_ITEM') {
        return { type: item.type, manualContent: item.manualContent }
      }
      return {
        type: item.type,
        item: String(item.value?._id || ''),
        subcategories: item.subcategories?.map(
          (subcategory) => subcategory._id,
        ),
      }
    })
    const payload = {
      sectionType,
      stacked:
        sectionType === 'MANUAL_CURATION'
          ? hasOnlyListMenus(items) || stacked
          : false,
      isScrollable: sectionType === 'MARKETING_BANNER' ? true : isScrollable,
      title,
      description: description || undefined,
      status: editingSection?.status || ('DRAFT' as const),
      contentTypes:
        sectionType === 'AUTOMATED_CURATION' ||
        sectionType === 'CATEGORY_CURATION' ||
        sectionType === 'SUBCATEGORY_CURATION'
          ? (['PRODUCTS'] as CurationContentType[])
          : sectionType === 'KITCHENS_NEAR_YOU'
            ? (['CREATORS'] as CurationContentType[])
            : Array.from(new Set(editorItems.map((item) => item.type))),
      items:
        sectionType === 'KITCHENS_NEAR_YOU' ||
        sectionType === 'AUTOMATED_CURATION' ||
        sectionType === 'CATEGORY_CURATION' ||
        sectionType === 'SUBCATEGORY_CURATION'
          ? undefined
          : items,
      filters:
        sectionType === 'AUTOMATED_CURATION'
          ? { sort, maxItems, salesLookbackDays, freeOnly }
          : sectionType === 'KITCHENS_NEAR_YOU'
            ? { maxItems }
            : undefined,
      subcategoryCuration:
        sectionType === 'SUBCATEGORY_CURATION'
          ? {
              subcategory: selectedSubcategoryId,
            }
          : undefined,
      categoryCuration:
        sectionType === 'CATEGORY_CURATION'
          ? {
              category: selectedCategoryId,
            }
          : undefined,
    }

    try {
      if (editingSection) {
        await updateSection({
          curationTypeId: activeCurationTypeId,
          sectionId: editingSection._id,
          payload,
          fulfilmentCentreId,
          page,
        }).unwrap()
        toast.success('Section updated')
      } else {
        await createSection({
          curationTypeId: activeCurationTypeId,
          payload,
          fulfilmentCentreId,
          page,
        }).unwrap()
        toast.success('Section saved as a draft')
      }
      setShowEditor(false)
      resetEditor()
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  const moveSection = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= sections.length) return
    setSections((current) => {
      const next = [...current]
      ;[next[index], next[target]] = [next[target], next[index]]
      return next
    })
  }

  const saveOrder = async () => {
    if (!activeCurationTypeId || !fulfilmentCentreId) return
    try {
      await reorderSections({
        curationTypeId: activeCurationTypeId,
        sectionIds: sections.map((section) => section._id),
        fulfilmentCentreId,
        page,
      }).unwrap()
      toast.success('Section order saved')
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  const setSectionStatus = async (
    section: HomepageSection,
    status: 'PUBLISHED' | 'ARCHIVED',
  ) => {
    if (!activeCurationTypeId || !fulfilmentCentreId) return
    try {
      if (status === 'PUBLISHED') {
        await publishSections({
          curationTypeId: activeCurationTypeId,
          sectionIds: [section._id],
          fulfilmentCentreId,
          page,
        }).unwrap()
      } else {
        await updateSection({
          curationTypeId: activeCurationTypeId,
          sectionId: section._id,
          payload: { status },
          fulfilmentCentreId,
          page,
        }).unwrap()
      }
      toast.success(status === 'PUBLISHED' ? 'Section published' : 'Archived')
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  const removeSection = async () => {
    if (!activeCurationTypeId || !fulfilmentCentreId || !sectionPendingDeletion)
      return

    try {
      await deleteSection({
        curationTypeId: activeCurationTypeId,
        sectionId: sectionPendingDeletion._id,
        fulfilmentCentreId,
        page,
      }).unwrap()
      toast.success('Section deleted')
      setSectionPendingDeletion(undefined)
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  const toggleScheduledSection = (sectionId: string) => {
    setSchedule((current) => ({
      ...current,
      [sectionId]: {
        selected: !current[sectionId]?.selected,
        startTime: current[sectionId]?.startTime || '06:00',
        endTime: current[sectionId]?.endTime || '11:00',
      },
    }))
  }

  const updateSchedule = (
    sectionId: string,
    field: 'startTime' | 'endTime',
    value: string,
  ) => {
    setSchedule((current) => ({
      ...current,
      [sectionId]: {
        selected: current[sectionId]?.selected || false,
        startTime: current[sectionId]?.startTime || '06:00',
        endTime: current[sectionId]?.endTime || '11:00',
        [field]: value,
      },
    }))
  }

  const resetScheduleEditor = () => {
    setShowScheduleEditor(false)
    setSchedule({})
    setGroupName('')
    setGroupOrder(1)
    setEditingGroupName(undefined)
  }

  const openCreateGroup = () => {
    setEditingGroupName(undefined)
    setSchedule({})
    setGroupName('')
    setGroupOrder(1)
    setShowScheduleEditor(true)
  }

  const openEditGroup = (group: {
    name: string
    order: number
    sections: HomepageSection[]
  }) => {
    setEditingGroupName(group.name)
    setGroupName(group.name)
    setGroupOrder(group.order || 1)
    const nextSchedule: Record<string, ScheduleDraft> = {}
    for (const section of group.sections) {
      nextSchedule[section._id] = {
        selected: true,
        startTime: section.timeSchedule?.startTime || '06:00',
        endTime: section.timeSchedule?.endTime || '11:00',
      }
    }
    setSchedule(nextSchedule)
    setShowScheduleEditor(true)
  }

  const submitSchedule = async (event: FormEvent) => {
    event.preventDefault()
    if (!activeCurationTypeId || !fulfilmentCentreId) return
    const selectedSections = sections
      .filter((section) => schedule[section._id]?.selected)
      .map((section) => ({
        sectionId: section._id,
        startTime: schedule[section._id].startTime,
        endTime: schedule[section._id].endTime,
      }))
    if (selectedSections.length < 2) {
      toast.error('Select at least two sections for a time-based group')
      return
    }
    try {
      if (editingGroupName) {
        await updateTimeGroup({
          curationTypeId: activeCurationTypeId,
          groupName: editingGroupName,
          payload: {
            groupName: groupName || undefined,
            targetOrder: groupOrder,
            sections: selectedSections,
          },
          fulfilmentCentreId,
          page,
        }).unwrap()
        toast.success('Time-based group updated')
      } else {
        await createTimeGroup({
          curationTypeId: activeCurationTypeId,
          payload: {
            groupName: groupName || undefined,
            targetOrder: groupOrder,
            sections: selectedSections,
          },
          fulfilmentCentreId,
          page,
        }).unwrap()
        toast.success('Time-based group saved')
      }
      resetScheduleEditor()
    } catch (error) {
      toast.error(errorMessage(error))
    }
  }

  const contentButtons =
    sectionType === 'MARKETING_BANNER'
      ? (['HOMEPAGE_BANNER'] as CurationContentType[])
      : sectionType === 'CATEGORY'
        ? (['CATEGORIES'] as CurationContentType[])
        : sectionType === 'SUBCATEGORY'
          ? (['SUBCATEGORIES'] as CurationContentType[])
          : sectionType === 'CATEGORY_CURATION'
            ? (['CATEGORIES'] as CurationContentType[])
            : sectionType === 'SUBCATEGORY_CURATION'
              ? (['SUBCATEGORIES'] as CurationContentType[])
              : selectableContentTypes
  const isCatalogueCuration =
    sectionType === 'CATEGORY_CURATION' ||
    sectionType === 'SUBCATEGORY_CURATION'

  return (
    <Layout>
      <div className="flex flex-col gap-7">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="mb-2 flex items-center gap-2">
              <PageHeader
                title={
                  page === CurationPage.BROWSE
                    ? 'Browse Curation'
                    : 'Good Online Homepage'
                }
                className="!mb-0"
              />
              <span className="rounded-full bg-[#E9F7EE] px-2.5 py-1 text-xs font-semibold text-[#16763B]">
                2.1
              </span>
            </div>
            <p className="text-sm text-[#666]">
              Curate each channel independently and preview what customers see.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-52 text-xs font-medium text-[#555]">
              Fulfilment centre
              <select
                className={`${inputClass} mt-1`}
                data-testid="fulfilment-centre-selector"
                disabled={fulfilmentCentres.length === 0}
                onChange={(event) => setFulfilmentCentreId(event.target.value)}
                value={fulfilmentCentreId}
              >
                {fulfilmentCentres.length === 0 ? (
                  <option value="">No fulfilment centres</option>
                ) : null}
                {fulfilmentCentres.map((centre) => (
                  <option key={centre._id} value={centre._id}>
                    {centre.name}
                  </option>
                ))}
              </select>
            </label>
            <label className="min-w-52 text-xs font-medium text-[#555]">
              Curation type
              <select
                className={`${inputClass} mt-1`}
                data-testid="curation-type-selector"
                disabled={isLoadingCurationTypes || curationTypes.length === 0}
                onChange={(event) =>
                  setActiveCurationTypeId(event.target.value)
                }
                value={activeCurationTypeId}
              >
                <option value="">Select curation type</option>
                {curationTypes.map((type) => (
                  <option key={type._id} value={type._id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </label>
            <button
              className={secondaryButtonClass}
              disabled={!activeCurationTypeId || !fulfilmentCentreId}
              onClick={openCreateGroup}
              type="button"
            >
              <Clock3 className="mr-2 inline h-4 w-4" />
              New time group
            </button>
            <button
              className={primaryButtonClass}
              disabled={!activeCurationTypeId || !fulfilmentCentreId}
              onClick={openCreateEditor}
              type="button"
            >
              <Plus className="mr-2 inline h-4 w-4" />
              Add section
            </button>
          </div>
        </div>

        {hasCurationTypeError ? (
          <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
            Curation types could not be loaded.
            <button
              className="ml-2 font-semibold underline"
              onClick={() => refetchTypes()}
              type="button"
            >
              Retry
            </button>
          </div>
        ) : null}

        <div className="flex gap-2 overflow-x-auto border-b border-[#E7E7E7]">
          {curationTypes.map((type) => (
            <button
              className={`whitespace-nowrap border-b-2 px-4 py-3 text-sm font-medium ${
                activeCurationTypeId === type._id
                  ? 'border-black text-black'
                  : 'border-transparent text-[#777]'
              }`}
              key={type._id}
              onClick={() => setActiveCurationTypeId(type._id)}
              type="button"
            >
              {type.name}
            </button>
          ))}
        </div>

        {showEditor ? (
          <form
            className="rounded-xl border border-[#DADADA] bg-white p-5 shadow-sm"
            onSubmit={submitSection}
            ref={editorRef}
          >
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold">
                  {editingSection ? 'Edit section' : 'New section'}
                </h2>
                <p className="mt-1 text-sm text-[#666]">
                  Configure the section and preview its selected content.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowEditor(false)
                  resetEditor()
                }}
                type="button"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <label className="text-sm font-medium">
                Section name
                <input
                  className={`${inputClass} mt-2`}
                  onChange={(event) => setTitle(event.target.value)}
                  required
                  value={title}
                />
              </label>
              <label className="text-sm font-medium">
                Description
                <input
                  className={`${inputClass} mt-2`}
                  onChange={(event) => setDescription(event.target.value)}
                  value={description}
                />
              </label>
              <label className="text-sm font-medium">
                Section type
                <select
                  className={`${inputClass} mt-2`}
                  onChange={(event) =>
                    changeSectionType(event.target.value as HomepageSectionType)
                  }
                  value={sectionType}
                >
                  {Object.entries(sectionTypeLabels)
                    .filter(
                      ([value]) =>
                        value !== 'KITCHENS_NEAR_YOU' ||
                        (page === CurationPage.BROWSE &&
                          activeCurationType?.productSection === 'FOOD'),
                    )
                    .map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                </select>
              </label>
              {sectionType === 'MANUAL_CURATION' &&
              !hasOnlyListMenus(editorItems) ? (
                <label className="flex items-center gap-3 rounded-xl border border-[#E4E4E4] bg-[#FAFAFA] px-4 py-3 text-sm font-medium md:col-span-3">
                  <input
                    checked={stacked}
                    className="h-4 w-4"
                    onChange={(event) => setStacked(event.target.checked)}
                    type="checkbox"
                  />
                  <span>
                    Render this manual section as stacked
                    <span className="block text-xs font-normal text-[#666]">
                      Use stacked when the banner should sit beside supporting
                      cards. Leave off for the normal row format.
                    </span>
                  </span>
                </label>
              ) : null}
              {sectionType !== 'MARKETING_BANNER' ? (
                <div className="rounded-xl border border-[#E4E4E4] bg-[#FAFAFA] px-4 py-3 md:col-span-3">
                  <label className="flex items-center gap-3 text-sm font-medium">
                    <input
                      checked={!isScrollable}
                      className="h-4 w-4"
                      onChange={(event) =>
                        setIsScrollable(!event.target.checked)
                      }
                      type="checkbox"
                    />
                    <span>
                      Use a fixed, non-scrollable card layout
                      <span className="block text-xs font-normal text-[#666]">
                        The customer site controls how many cards fit this
                        layout and can show a See all action for the remaining
                        items.
                      </span>
                    </span>
                  </label>
                </div>
              ) : null}
            </div>

            {sectionType === 'KITCHENS_NEAR_YOU' ? (
              <div className="mt-6 space-y-4 rounded-xl bg-[#F7F7F7] p-5">
                <p className="text-sm text-[#666]">
                  Available only for Food Browse. Admin shows all kitchens in
                  the selected storefront. Customers see only open kitchens with
                  food available for delivery to their location. Kitchens are
                  selected automatically; there is no manual creator selection.
                </p>
                <label className="block text-sm font-medium">
                  Maximum kitchens shown to customers
                  <input
                    className={`${inputClass} mt-2`}
                    type="number"
                    min={1}
                    max={12}
                    value={maxItems}
                    onChange={(event) =>
                      setMaxItems(
                        Math.min(
                          12,
                          Math.max(1, Number(event.target.value) || 1),
                        ),
                      )
                    }
                  />
                </label>
              </div>
            ) : sectionType === 'AUTOMATED_CURATION' ? (
              <div className="mt-6 grid gap-4 rounded-xl bg-[#F7F7F7] p-5 md:grid-cols-3">
                <label className="text-sm font-medium">
                  Sort products by
                  <select
                    className={`${inputClass} mt-2`}
                    onChange={(event) =>
                      setSort(event.target.value as AutomatedCurationSort)
                    }
                    value={sort}
                  >
                    {getSortOptions(activeCurationType?.productSection).map(
                      ({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ),
                    )}
                  </select>
                </label>
                <label className="text-sm font-medium">
                  Number of items
                  <select
                    className={`${inputClass} mt-2`}
                    onChange={(event) =>
                      setMaxItems(Number(event.target.value))
                    }
                    value={maxItems}
                  >
                    {[4, 6, 8, 10, 12].map((limit) => (
                      <option key={limit} value={limit}>
                        {limit}
                      </option>
                    ))}
                  </select>
                </label>
                {sort === 'sales_volume' ? (
                  <label className="text-sm font-medium">
                    Sales lookback (days)
                    <input
                      className={`${inputClass} mt-2`}
                      max={365}
                      min={1}
                      onChange={(event) =>
                        setSalesLookbackDays(
                          Math.min(
                            365,
                            Math.max(1, Number(event.target.value) || 1),
                          ),
                        )
                      }
                      type="number"
                      value={salesLookbackDays}
                    />
                  </label>
                ) : null}
                <label className="flex items-center gap-3 text-sm font-medium md:col-span-3">
                  <input
                    checked={freeOnly}
                    className="h-4 w-4"
                    onChange={(event) => setFreeOnly(event.target.checked)}
                    type="checkbox"
                  />
                  <span>
                    Free only
                    <span className="block text-xs font-normal text-[#666]">
                      Only show items with no paid ticket tier or a price of
                      zero.
                    </span>
                  </span>
                </label>
              </div>
            ) : (
              <>
                <div className="mt-6">
                  <div className="mb-3">
                    <h3 className="font-semibold">
                      {sectionType === 'CATEGORY_CURATION'
                        ? 'Select category'
                        : sectionType === 'SUBCATEGORY_CURATION'
                          ? 'Select subcategory'
                          : 'Select content'}
                    </h3>
                    <p className="text-xs text-[#666]">
                      {sectionType === 'CATEGORY_CURATION'
                        ? 'Select one active category. Our picks are used when configured; otherwise all products in the category are shown.'
                        : sectionType === 'SUBCATEGORY_CURATION'
                          ? 'Select one active subcategory. Our picks are used when configured; otherwise all products in the subcategory are shown.'
                          : 'Search and select records; IDs are handled automatically.'}
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {contentButtons.map((type) => {
                      const count = editorItems.filter(
                        (item) => item.type === type,
                      ).length
                      const bannerLimit =
                        sectionType === 'MARKETING_BANNER' ? 3 : 1
                      const disabled =
                        type === 'HOMEPAGE_BANNER' && count >= bannerLimit
                      return (
                        <button
                          className={secondaryButtonClass}
                          disabled={disabled}
                          key={type}
                          onClick={() => openContentPicker(type)}
                          type="button"
                        >
                          <Plus className="mr-1 inline h-4 w-4" />
                          {contentTypeLabels[type]}
                          {count ? ` (${count})` : ''}
                        </button>
                      )
                    })}
                  </div>

                  {editorItems.length ? (
                    <>
                      {!isCatalogueCuration ? (
                        <p className="mt-5 text-xs text-[#777]">
                          Drag items to change the order customers see them in.
                        </p>
                      ) : null}
                      <DndContext
                        collisionDetection={closestCenter}
                        modifiers={[restrictToVerticalAxis]}
                        onDragEnd={handleItemDragEnd}
                      >
                        <SortableContext
                          items={editorItems.map((item) => item.key)}
                          strategy={verticalListSortingStrategy}
                        >
                          <div className="mt-2 space-y-2">
                            {editorItems.map((item) => (
                              <div key={item.key}>
                                <SortableEditorItem
                                  item={item}
                                  onEdit={() =>
                                    openContentPicker(item.type, item.key)
                                  }
                                  onRemove={() => removeEditorItem(item.key)}
                                />
                                {page === CurationPage.BROWSE &&
                                sectionType === 'CATEGORY' &&
                                item.type === 'CATEGORIES' ? (
                                  <div className="ml-8 mt-2 flex items-center gap-3 pb-2">
                                    <button
                                      type="button"
                                      className={secondaryButtonClass}
                                      onClick={() =>
                                        setSubcategoryCardKey(item.key)
                                      }
                                    >
                                      Select subcategories
                                    </button>
                                    <span className="text-xs text-[#666]">
                                      {item.subcategories
                                        ?.map((subcategory) => subcategory.name)
                                        .join(', ') ||
                                        'No subcategories selected'}
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                            ))}
                          </div>
                        </SortableContext>
                      </DndContext>
                    </>
                  ) : null}
                </div>
              </>
            )}

            <div className="mt-7">
              <div className="mb-3 flex items-center justify-between gap-3">
                <p className="text-sm font-semibold">
                  {sectionType === 'KITCHENS_NEAR_YOU'
                    ? 'Kitchen catalogue'
                    : 'Customer preview'}
                </p>
                <button
                  aria-expanded={!isCustomerPreviewCollapsed}
                  className={secondaryButtonClass}
                  onClick={() =>
                    setIsCustomerPreviewCollapsed((collapsed) => !collapsed)
                  }
                  type="button"
                >
                  {isCustomerPreviewCollapsed ? (
                    <ChevronRight className="mr-1 inline h-4 w-4" />
                  ) : (
                    <ChevronDown className="mr-1 inline h-4 w-4" />
                  )}
                  {isCustomerPreviewCollapsed ? 'Show preview' : 'Hide preview'}
                </button>
              </div>
              {!isCustomerPreviewCollapsed ? (
                sectionType === 'KITCHENS_NEAR_YOU' ? (
                  isPreviewingKitchens ? (
                    <p className="p-6 text-sm text-[#666]">Loading kitchens…</p>
                  ) : kitchenPreviewFailed ? (
                    <div role="alert" className="p-6 text-sm">
                      <p>Unable to load kitchens.</p>
                      <button
                        type="button"
                        className="mt-2 underline"
                        onClick={() => {
                          void refetchKitchenPreview()
                        }}
                      >
                        Try again
                      </button>
                    </div>
                  ) : (
                    <CustomerSectionPreview
                      items={kitchenPreview?.items || []}
                      title={title}
                      description={description}
                      sectionType={sectionType}
                      isScrollable={isScrollable}
                    />
                  )
                ) : sectionType === 'AUTOMATED_CURATION' ? (
                  <div>
                    <p className="mb-3 text-xs text-[#666]">
                      {automatedPreviewSummary({
                        count: isPreviewingAutomated
                          ? undefined
                          : automatedPreview?.count,
                        maxItems,
                        sort,
                        salesLookbackDays,
                        freeOnly,
                        curationType: activeCurationType,
                      })}
                    </p>
                    {isPreviewingAutomated ? (
                      <p className="rounded-xl bg-[#F7F7F7] p-8 text-center text-sm text-[#666]">
                        Loading preview…
                      </p>
                    ) : automatedPreview && automatedPreview.items.length ? (
                      <CustomerSectionPreview
                        items={automatedPreview.items.slice(0, maxItems)}
                        sectionType={sectionType}
                        isScrollable={isScrollable}
                      />
                    ) : (
                      <p className="rounded-xl bg-[#F7F7F7] p-8 text-center text-sm text-[#666]">
                        No products match these filters yet.
                      </p>
                    )}
                  </div>
                ) : sectionType === 'CATEGORY_CURATION' ? (
                  <div>
                    <p className="mb-3 text-xs text-[#666]">
                      {selectedCategory
                        ? `${categoryPreview?.count || 0} products from ${
                            selectedCategory.name || 'selected category'
                          } · ${
                            categoryPreview?.source === 'OUR_PICKS'
                              ? 'Our picks'
                              : 'All products'
                          }`
                        : 'Select a category to preview its products.'}
                    </p>
                    {isPreviewingCategory ? (
                      <p className="rounded-xl bg-[#F7F7F7] p-8 text-center text-sm text-[#666]">
                        Loading preview…
                      </p>
                    ) : categoryPreviewError ? (
                      <p className="rounded-xl bg-red-50 p-8 text-center text-sm text-red-700">
                        {errorMessage(categoryPreviewError)}
                      </p>
                    ) : categoryPreview?.items.length ? (
                      <CustomerSectionPreview
                        items={categoryPreview.items}
                        sectionType={sectionType}
                        isScrollable={isScrollable}
                      />
                    ) : (
                      <p className="rounded-xl bg-[#F7F7F7] p-8 text-center text-sm text-[#666]">
                        No active products match this source yet.
                      </p>
                    )}
                  </div>
                ) : sectionType === 'SUBCATEGORY_CURATION' ? (
                  <div>
                    <p className="mb-3 text-xs text-[#666]">
                      {selectedSubcategory
                        ? `${subcategoryPreview?.count || 0} products from ${
                            selectedSubcategory.name || 'selected subcategory'
                          } · ${
                            subcategoryPreview?.source === 'OUR_PICKS'
                              ? 'Our picks'
                              : 'All products'
                          }`
                        : 'Select a subcategory to preview its products.'}
                    </p>
                    {isPreviewingSubcategory ? (
                      <p className="rounded-xl bg-[#F7F7F7] p-8 text-center text-sm text-[#666]">
                        Loading preview…
                      </p>
                    ) : subcategoryPreviewError ? (
                      <p className="rounded-xl bg-red-50 p-8 text-center text-sm text-red-700">
                        {errorMessage(subcategoryPreviewError)}
                      </p>
                    ) : subcategoryPreview?.items.length ? (
                      <CustomerSectionPreview
                        items={subcategoryPreview.items}
                        sectionType={sectionType}
                        isScrollable={isScrollable}
                      />
                    ) : (
                      <p className="rounded-xl bg-[#F7F7F7] p-8 text-center text-sm text-[#666]">
                        No active products match this source yet.
                      </p>
                    )}
                  </div>
                ) : (
                  <CustomerSectionPreview
                    items={editorItemsToPreview(editorItems)}
                    title={title}
                    description={description}
                    sectionType={sectionType}
                    isScrollable={isScrollable}
                    stacked={stacked}
                  />
                )
              ) : null}
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                className={secondaryButtonClass}
                onClick={() => {
                  setShowEditor(false)
                  resetEditor()
                }}
                type="button"
              >
                Cancel
              </button>
              <button
                className={primaryButtonClass}
                disabled={isCreatingSection || isUpdatingSection}
                type="submit"
              >
                {editingSection ? 'Save changes' : 'Save draft'}
              </button>
            </div>
          </form>
        ) : null}

        {showScheduleEditor ? (
          <form
            className="rounded-xl border border-[#DADADA] bg-white p-5 shadow-sm"
            onSubmit={submitSchedule}
          >
            <div className="mb-5 flex items-start justify-between">
              <div>
                <h2 className="text-lg font-semibold">
                  {editingGroupName
                    ? `Edit time group: ${editingGroupName}`
                    : 'New time-based group'}
                </h2>
                <p className="mt-1 text-sm text-[#666]">
                  Select existing sections and assign non-overlapping windows.
                  {editingGroupName
                    ? ' Unchecking a section removes it from this group.'
                    : ''}
                </p>
              </div>
              <button onClick={resetScheduleEditor} type="button">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="mb-4 grid gap-3 md:grid-cols-2">
              <input
                className={inputClass}
                onChange={(event) => setGroupName(event.target.value)}
                placeholder="Group name, e.g. food-meal-times"
                value={groupName}
              />
              <label className="flex items-center gap-3 text-sm">
                Homepage position
                <input
                  className={inputClass}
                  min={1}
                  onChange={(event) =>
                    setGroupOrder(Number(event.target.value))
                  }
                  type="number"
                  value={groupOrder}
                />
              </label>
            </div>
            <div className="space-y-2">
              {sections.map((section) => {
                const slot = schedule[section._id]
                return (
                  <div
                    className="grid items-center gap-3 rounded-lg border border-[#E7E7E7] p-3 md:grid-cols-[1fr_150px_150px]"
                    key={section._id}
                  >
                    <label className="flex items-center gap-3 text-sm font-medium">
                      <input
                        checked={slot?.selected || false}
                        onChange={() => toggleScheduledSection(section._id)}
                        type="checkbox"
                      />
                      {section.title}
                    </label>
                    <input
                      className={inputClass}
                      disabled={!slot?.selected}
                      onChange={(event) =>
                        updateSchedule(
                          section._id,
                          'startTime',
                          event.target.value,
                        )
                      }
                      type="time"
                      value={slot?.startTime || '06:00'}
                    />
                    <input
                      className={inputClass}
                      disabled={!slot?.selected}
                      onChange={(event) =>
                        updateSchedule(
                          section._id,
                          'endTime',
                          event.target.value,
                        )
                      }
                      type="time"
                      value={slot?.endTime || '11:00'}
                    />
                  </div>
                )
              })}
            </div>
            <div className="mt-5 flex justify-end">
              <button
                className={primaryButtonClass}
                disabled={isScheduling || isUpdatingGroup}
                type="submit"
              >
                {editingGroupName ? 'Update time group' : 'Save time group'}
              </button>
            </div>
          </form>
        ) : null}

        {timeGroups.length ? (
          <div className="rounded-xl border border-[#E7E7E7] bg-[#FAFAFA] p-5">
            <div className="mb-3 flex items-center gap-2">
              <Clock3 className="h-4 w-4 text-[#555]" />
              <h3 className="font-semibold">Time-based groups</h3>
            </div>
            <div className="space-y-3">
              {timeGroups.map((group) => (
                <div
                  className="rounded-lg border border-[#E7E7E7] bg-white p-4"
                  key={group.name}
                >
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold">{group.name}</p>
                      <p className="text-xs text-[#777]">
                        Position {group.order} · {group.sections.length}{' '}
                        sections · only the active window shows
                      </p>
                    </div>
                    <button
                      className={secondaryButtonClass}
                      onClick={() => openEditGroup(group)}
                      type="button"
                    >
                      <Edit3 className="mr-1 inline h-4 w-4" />
                      Edit group
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {[...group.sections]
                      .sort((a, b) =>
                        (a.timeSchedule?.startTime || '').localeCompare(
                          b.timeSchedule?.startTime || '',
                        ),
                      )
                      .map((section) => (
                        <span
                          className="rounded-full bg-[#F1F1F1] px-2.5 py-1 text-[11px]"
                          key={section._id}
                        >
                          {section.title} · {section.timeSchedule?.startTime}–
                          {section.timeSchedule?.endTime}
                        </span>
                      ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold">
              {activeCurationType?.name || 'Sections'}
              <span className="ml-2 rounded-full bg-[#F1F1F1] px-2.5 py-0.5 text-xs font-medium text-[#555]">
                {fulfilmentCentres.find(
                  (centre) => centre._id === fulfilmentCentreId,
                )?.name || 'Fulfilment centre'}
              </span>
            </h2>
            <p className="mt-1 text-sm text-[#666]">
              {homepageConfig?.publishedSections || 0} published ·{' '}
              {homepageConfig?.draftSections || 0} drafts
            </p>
          </div>
          <button
            className={secondaryButtonClass}
            disabled={isReordering || sections.length === 0}
            onClick={saveOrder}
            type="button"
          >
            Save order
          </button>
        </div>

        {isLoading || isFetching ? (
          <div className="rounded-xl border p-10 text-center text-sm text-[#666]">
            Loading sections…
          </div>
        ) : null}

        {!isLoading && !isFetching && sections.length === 0 ? (
          <div className="rounded-xl border border-dashed p-12 text-center">
            <p className="font-semibold">No sections in this curation type</p>
            <p className="mt-1 text-sm text-[#777]">
              Add a section to curate the selected centre&apos;s homepage.
            </p>
          </div>
        ) : null}

        <div className="space-y-5">
          {sections.map((section, index) => (
            <article
              className="overflow-hidden rounded-xl border border-[#E7E7E7] bg-white"
              key={section._id}
            >
              <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4">
                <div className="flex items-start gap-3">
                  <button
                    aria-expanded={!collapsedSections[section._id]}
                    aria-label={
                      collapsedSections[section._id]
                        ? 'Expand section'
                        : 'Collapse section'
                    }
                    className="mt-0.5 rounded-lg p-1 text-[#555] hover:bg-[#F3F3F3]"
                    onClick={() => toggleSectionCollapsed(section._id)}
                    type="button"
                  >
                    {collapsedSections[section._id] ? (
                      <ChevronRight className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <h3 className="font-semibold">{section.title}</h3>
                      <span className="rounded-full bg-[#F1F1F1] px-2 py-0.5 text-[11px]">
                        {sectionTypeLabels[section.sectionType]}
                      </span>
                      <span className="rounded-full bg-[#F1F1F1] px-2 py-0.5 text-[11px]">
                        {section.status}
                      </span>
                      {section.sectionType === 'MANUAL_CURATION' ? (
                        <span className="rounded-full bg-[#F7F0E8] px-2 py-0.5 text-[11px] text-[#8A4B00]">
                          {hasOnlyListMenus(section.items) || section.stacked
                            ? 'Stacked'
                            : 'Row'}
                        </span>
                      ) : null}
                      {section.isScrollable === false ? (
                        <span className="rounded-full bg-[#EEF7F0] px-2 py-0.5 text-[11px] text-[#16763B]">
                          Non-scrollable
                        </span>
                      ) : null}
                      {section.isTimeScheduled ? (
                        <span className="rounded-full bg-[#EAF1FF] px-2 py-0.5 text-[11px] text-[#275EB8]">
                          {section.timeSchedule?.startTime}–
                          {section.timeSchedule?.endTime}
                        </span>
                      ) : null}
                      {section.isTimeScheduled &&
                      section.timeSchedule?.slotGroup ? (
                        <span className="rounded-full bg-[#EEF7F0] px-2 py-0.5 text-[11px] text-[#16763B]">
                          {section.timeSchedule.slotGroup}
                        </span>
                      ) : null}
                    </div>
                    <p className="mt-1 text-xs text-[#777]">
                      Position {index + 1} · {section.items.length} items
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap items-center gap-1">
                  <button
                    className={secondaryButtonClass}
                    onClick={() => openEditEditor(section)}
                    type="button"
                  >
                    <Edit3 className="mr-1 inline h-4 w-4" />
                    Edit
                  </button>
                  <button
                    className={secondaryButtonClass}
                    disabled={index === 0}
                    onClick={() => moveSection(index, -1)}
                    type="button"
                  >
                    <ArrowUp className="h-4 w-4" />
                  </button>
                  <button
                    className={secondaryButtonClass}
                    disabled={index === sections.length - 1}
                    onClick={() => moveSection(index, 1)}
                    type="button"
                  >
                    <ArrowDown className="h-4 w-4" />
                  </button>
                  <button
                    className={secondaryButtonClass}
                    onClick={() =>
                      setSectionStatus(
                        section,
                        section.status === 'PUBLISHED'
                          ? 'ARCHIVED'
                          : 'PUBLISHED',
                      )
                    }
                    type="button"
                  >
                    {section.status === 'PUBLISHED' ? 'Archive' : 'Publish'}
                  </button>
                  <button
                    aria-label={`Delete ${section.title}`}
                    className="rounded-lg p-2 text-[#A22] hover:bg-red-50"
                    onClick={() => setSectionPendingDeletion(section)}
                    type="button"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              {collapsedSections[section._id] ? null : (
                <div className="p-5">
                  {section.sectionType === 'AUTOMATED_CURATION' ? (
                    <AutomatedSectionPreview
                      curationType={activeCurationType}
                      filters={section.filters}
                      fulfilmentCentreId={fulfilmentCentreId}
                      page={page}
                      isScrollable={section.isScrollable}
                    />
                  ) : (
                    <CustomerSectionPreview
                      items={section.items}
                      title={section.title}
                      description={section.description}
                      sectionType={section.sectionType}
                      isScrollable={section.isScrollable}
                      stacked={Boolean(section.stacked)}
                    />
                  )}
                </div>
              )}
            </article>
          ))}
        </div>
      </div>

      <ConfirmationModal
        open={Boolean(sectionPendingDeletion)}
        setOpen={(open) => {
          if (!open) setSectionPendingDeletion(undefined)
        }}
        onCloseCallback={() => setSectionPendingDeletion(undefined)}
        title={`Delete '${sectionPendingDeletion?.title || ''}' Section`}
        message="Are you sure you want to delete this section? This action cannot be undone."
        onConfirm={removeSection}
        loading={isDeletingSection}
        confirmText="Confirm"
      />

      {subcategoryCardKey ? (
        <SelectSubCategoriesModal
          open={Boolean(subcategoryCardKey)}
          setOpen={(open) => {
            if (!open) setSubcategoryCardKey(undefined)
          }}
          activeOnly
          section={activeCurationType?.productSection}
          categories={[
            String(
              editorItems.find((item) => item.key === subcategoryCardKey)?.value
                ?._id || '',
            ),
          ]}
          selectedSubCategories={
            editorItems.find((item) => item.key === subcategoryCardKey)
              ?.subcategories || []
          }
          onSuccessCallback={(subcategories) => {
            setEditorItems((items) =>
              items.map((item) =>
                item.key === subcategoryCardKey
                  ? { ...item, subcategories }
                  : item,
              ),
            )
            setSubcategoryCardKey(undefined)
          }}
        />
      ) : null}
      <ContentPickerModals
        bannerEditKey={bannerEditKey}
        fulfilmentCentre={fulfilmentCentres.find(
          (centre) => centre._id === fulfilmentCentreId,
        )}
        items={editorItems}
        pickerType={pickerType}
        maxCategorySelection={sectionType === 'CATEGORY_CURATION' ? 1 : 12}
        maxSubcategorySelection={
          sectionType === 'SUBCATEGORY_CURATION' ? 1 : 12
        }
        productSection={activeCurationType?.productSection}
        replaceType={replaceType}
        saveBanner={saveBanner}
        saveManualItem={saveManualItem}
        setPickerType={(type) => {
          setPickerType(type)
          if (!type) setBannerEditKey(undefined)
        }}
      />
    </Layout>
  )
}
